/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { queryCache } from 'react-query';

import { Endpoints, Infer } from 'shared';

import { ApiClient } from '../types/api/ApiClient';
import { getApiEndpoint } from '../utils/getApiEndpoint';
import { apiClientFactory } from '../utils/http-client/apiClientFactory';
import { axiosInstanceFactory } from '../utils/http-client/axiosInstanceFactory';
import { axiosJwtTokenInterceptor, axiosUnauthorizedInterceptor } from '../utils/http-client/axiosInterceptors';
import { fetchTeam, fetchTeams } from '../utils/queryFns';

const invalidateQueriesAndReturn = async <T>(response: T) => {
  await queryCache.invalidateQueries(fetchTeams.key);
  await queryCache.invalidateQueries(fetchTeam.key);
  return response;
};

class TeamApi {
  private api: ApiClient;

  constructor() {
    this.api = apiClientFactory(
      axiosInstanceFactory(
        { baseURL: getApiEndpoint() },
        [axiosJwtTokenInterceptor],
        [[undefined, axiosUnauthorizedInterceptor]],
      ),
    );
  }

  fetchTeams = ({ organizationId }: { organizationId: string }) =>
    this.api.get<Infer<typeof Endpoints.GetTeams>['Response'][200]>(Endpoints.GetTeams.route({ organizationId }));

  fetchTeam = (props: { organizationId: string; teamIdentifier: string }) =>
    this.api.get<Infer<typeof Endpoints.GetTeam>['Response'][200]>(Endpoints.GetTeam.route(props));

  fetchSsoGroups = (props: { organizationId: string }) =>
    this.api.get<Infer<typeof Endpoints.GetSsoGroups>['Response'][200]>(Endpoints.GetSsoGroups.route(props));

  createTeam = ({ organizationId, data }: { organizationId: string; data: Infer<typeof Endpoints.PostTeam>['Body'] }) =>
    this.api
      .post<Infer<typeof Endpoints.PostTeam>['Response'][200]>(Endpoints.PostTeam.route({ organizationId }), data)
      .then(invalidateQueriesAndReturn);

  patchTeam = (props: {
    organizationId: string;
    teamIdentifier: string;
    data: Infer<typeof Endpoints.PatchTeam>['Body'];
  }) =>
    this.api
      .patch<
        Infer<typeof Endpoints.PatchTeam>['Response'][200]
      >(Endpoints.PatchTeam.route({ organizationId: props.organizationId, teamIdentifier: props.teamIdentifier }), props.data)
      .then(invalidateQueriesAndReturn);

  deleteTeam = ({ organizationId, teamIdentifier }: { organizationId: string; teamIdentifier: string }) =>
    this.api.del(Endpoints.DeleteTeam.route({ organizationId, teamIdentifier })).then(invalidateQueriesAndReturn);

  addTeamUser = (props: { organizationId: string; teamIdentifier: string; userIds: string[]; groupNames: string[] }) =>
    this.api
      .post<
        Infer<typeof Endpoints.PostTeamUsers>['Response'][200]
      >(Endpoints.PostTeamUsers.route({ organizationId: props.organizationId, teamIdentifier: props.teamIdentifier }), { users: props.userIds.map(id => ({ id })), ssoGroups: props.groupNames.map(groupName => ({ groupName })) })
      .then(invalidateQueriesAndReturn);

  removeTeamUser = (props: {
    organizationId: string;
    teamIdentifier: string;
    target: { userId: string } | { groupName: string };
  }) =>
    this.api
      .del(
        'userId' in props.target
          ? Endpoints.DeleteTeamUser.route({ ...props, userId: props.target.userId })
          : Endpoints.DeleteTeamGroup.route({
              ...props,
              urlEncodedGroupName: encodeURIComponent(props.target.groupName),
            }),
      )
      .then(invalidateQueriesAndReturn);
}

export const TeamApiService = new TeamApi();
